// covered-by: frontends/carrie/src/ask-v2/home/__tests__/AskV2Home.test.tsx
// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ChatPage.test.tsx
// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ProjectPage.test.tsx
// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewWorkspace.test.tsx
import {
  ArrowUp,
  Camera,
  CircleNotch,
  Check,
  Globe,
  LockSimple,
  Paperclip,
  Plus,
  Stop,
  X,
} from "@phosphor-icons/react";
import {
  Button,
  Flex,
  IconButton,
  Popover,
  Text,
  TextArea,
} from "@radix-ui/themes";
import {
  type ChangeEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { ASK_V2_FILE_ACCEPT } from "./acceptedFiles";
import { useRegisterChatFileDropTarget } from "./chatFileDropTarget";
import { CommentContextChip } from "./CommentContextChip";
import { type AskV2TransportApi } from "./useAskV2Transport";

import {
  MentionTextarea,
  type MentionMember,
} from "../../components/ask-puntt/MentionTextarea";
import { useAttachedFiles } from "../../hooks/useAttachedFiles";
import {
  AskV2ComposerModeToggle,
  type AskV2ComposerMode,
} from "../home/ComposerModeToggle";
import { useAskV2ReviewComposerContext } from "../review/AskV2ReviewComposerContext";
import { AskV2ReviewPaneTabs } from "../review/AskV2ReviewPaneTabs";
import { type AskV2Attachment } from "../services/chat";

type SummonBarProps = {
  transport: AskV2TransportApi;
  landing?: boolean;
  /** When true, block send until the route has bound a chat id (project feed). */
  requireBoundChatId?: boolean;
  composerMode?: AskV2ComposerMode;
  onComposerModeChange?: (mode: AskV2ComposerMode) => void;
  needsProjectSelection?: boolean;
  projectSubmitting?: boolean;
  onProjectSubmit?: (input: {
    message: string;
    attachments: AskV2Attachment[];
    webSearch: boolean;
  }) => void | boolean | Promise<void | boolean>;
  /** When set on thread composers, enables @mention invite autocomplete. */
  mentionMembers?: MentionMember[];
  rosterMembers?: MentionMember[];
  enableDirectoryMentions?: boolean;
  composerAriaLabel?: string;
  /** Read-only viewer: the server rejects their sends with 403, so the
      composer is locked with a lock notice instead of allowing a doomed send. */
  readOnly?: boolean;
};

export function AskV2SummonBar({
  transport,
  landing = false,
  requireBoundChatId = false,
  composerMode: composerModeProp,
  onComposerModeChange,
  needsProjectSelection = false,
  projectSubmitting = false,
  onProjectSubmit,
  mentionMembers,
  rosterMembers = [],
  enableDirectoryMentions = false,
  composerAriaLabel,
  readOnly = false,
}: SummonBarProps) {
  const { t } = useTranslation("ui");
  const { t: tCommon } = useTranslation("common");
  const reviewComposer = useAskV2ReviewComposerContext();
  const formRef = useRef<HTMLFormElement>(null);
  const composerFocusNonce = reviewComposer?.composerFocusNonce ?? 0;
  const appliedComposerFocusNonce = useRef(0);
  // why: `inFlight` is not in this lock. While an answer is being produced the
  // person may write their next message; `canSend` is what refuses to send it.
  // `sending` stays: the ACK that ends it clears the composer, and words typed
  // before it would be wiped.
  const composerLocked = readOnly || transport.sending || projectSubmitting;
  useLayoutEffect(() => {
    if (
      composerFocusNonce === 0 ||
      composerFocusNonce === appliedComposerFocusNonce.current
    ) {
      return;
    }
    const textarea = formRef.current?.querySelector("textarea");
    if (!textarea || textarea.disabled) return;
    appliedComposerFocusNonce.current = composerFocusNonce;
    textarea.focus();
    const end = textarea.value.length;
    textarea.setSelectionRange(end, end);
  }, [composerFocusNonce, composerLocked]);
  const [composerModeLocal, setComposerModeLocal] =
    useState<AskV2ComposerMode>("chat");
  const composerMode = composerModeProp ?? composerModeLocal;
  const setComposerMode = onComposerModeChange ?? setComposerModeLocal;
  const {
    attachedItems,
    addFiles,
    addFile,
    removeFile,
    clearAll,
    readyAttachments,
    hasPending,
  } = useAttachedFiles();
  useRegisterChatFileDropTarget(addFiles, !readOnly);
  const [mentions, setMentions] = useState<string[]>([]);
  const [mentionEmails, setMentionEmails] = useState<string[]>([]);
  const useMentionComposer = !landing && mentionMembers != null;
  const hasReadyAttachments = readyAttachments.length > 0;
  // why: incomplete transport mocks omitted commentContexts; `.length` threw.
  const quotedContexts = transport.quotedContexts ?? [];
  const commentContexts = transport.commentContexts ?? [];
  const openItemContexts = transport.openItemContexts ?? [];
  const hasQuotedContexts = quotedContexts.length > 0;
  const hasCommentContexts = commentContexts.length > 0;
  const hasOpenItemContexts = openItemContexts.length > 0;

  useEffect(() => {
    if (!transport.composer.trim()) {
      setMentions([]);
      setMentionEmails([]);
    }
  }, [transport.composer]);
  const canSend =
    !readOnly &&
    (Boolean(transport.composer.trim()) ||
      hasReadyAttachments ||
      hasQuotedContexts ||
      hasCommentContexts ||
      hasOpenItemContexts) &&
    !hasPending &&
    !transport.sending &&
    !transport.inFlight &&
    !projectSubmitting &&
    !needsProjectSelection &&
    (!requireBoundChatId || Boolean(transport.chatId));
  const placeholder = landing
    ? composerMode === "project"
      ? t("home.askInProject")
      : t("askAnything")
    : hasQuotedContexts || hasCommentContexts || hasOpenItemContexts
      ? t("askV2.transport.replyPlaceholder")
      : // why: on a review with nothing in it yet the found-something line is a
        // claim about a review that has not said anything -- the composer is
        // offered for what she wants to ask, not to browse findings that do not
        // exist. The review context is the only place `commentCount` is set.
        reviewComposer?.commentCount === 0
        ? t("askAnything")
        : t("askV2.transport.placeholder");

  const submit = () => {
    if (!canSend) return;
    const attachments = [...readyAttachments];
    const message = transport.composer;
    if (landing && composerMode === "project" && onProjectSubmit) {
      // why: mirror the chat path's composer reset, but only after a successful
      // post — a failed project post must keep both text and its attachments so
      // the user can retry. Clearing eagerly would drop already-uploaded files.
      void Promise.resolve(
        onProjectSubmit({
          message,
          attachments,
          webSearch: transport.webSearch,
        }),
      ).then((posted) => {
        if (posted) clearAll();
      });
      return;
    }
    clearAll();
    void transport.send({
      attachments,
      mentions: mentions.length > 0 ? mentions : undefined,
      mentionEmails: mentionEmails.length > 0 ? mentionEmails : undefined,
    });
  };

  return (
    <form
      ref={formRef}
      data-testid="ask-v2-summon-bar"
      className="flex w-full flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <div
        className={
          landing
            ? "relative z-20 overflow-hidden rounded-2xl border border-puntt-neutral-gray-6 bg-base-white shadow-lg transition-all duration-200 focus-within:border-puntt-accent-7"
            : "relative z-20 overflow-hidden rounded-2xl border border-puntt-neutral-gray-6 bg-base-white shadow-sm transition-shadow duration-200 focus-within:border-puntt-accent-7 focus-within:shadow-md focus-within:ring-2 focus-within:ring-puntt-accent-6/20"
        }
        data-testid={landing ? "ask-v2-home-composer" : "ask-v2-composer-card"}
      >
        {hasCommentContexts ? (
          <div
            className="flex w-full flex-col p-2 pb-1"
            data-testid="ask-v2-comment-contexts"
          >
            {commentContexts.map((ctx) => (
              <CommentContextChip
                key={ctx.id}
                variant="composer"
                context={ctx}
                testId={`ask-v2-comment-context-${ctx.id}`}
                onRemove={() => {
                  if (reviewComposer) {
                    reviewComposer.onRemoveCommentContext(ctx.id);
                    return;
                  }
                  transport.removeCommentContext(ctx.id);
                }}
              />
            ))}
          </div>
        ) : null}
        {hasOpenItemContexts ? (
          <Flex
            gap="2"
            wrap="wrap"
            className="p-2 pb-1"
            data-testid="ask-v2-open-item-contexts"
          >
            {openItemContexts.map((ctx) => (
              <Flex
                key={ctx.id}
                align="center"
                gap="1"
                className="max-w-full rounded-md bg-puntt-neutral-gray-3 px-2 py-1"
                data-testid={`ask-v2-open-item-context-${ctx.id}`}
              >
                <Text
                  size="1"
                  className="max-w-[240px] truncate text-puntt-neutral-gray-11"
                >
                  {ctx.needed}
                </Text>
                <button
                  type="button"
                  data-testid={`ask-v2-remove-open-item-context-${ctx.id}`}
                  onClick={() => transport.removeOpenItemContext(ctx.id)}
                  className="text-puntt-neutral-gray-9 hover:text-puntt-neutral-gray-12"
                  aria-label={t("askV2.transport.removeOpenItemContext")}
                >
                  ×
                </button>
              </Flex>
            ))}
          </Flex>
        ) : null}
        {hasQuotedContexts ? (
          <Flex
            gap="2"
            wrap="wrap"
            className="p-2"
            data-testid="ask-v2-quoted-contexts"
          >
            {quotedContexts.map((ctx) => (
              <Flex
                key={ctx.id}
                align="center"
                gap="1"
                className="max-w-full rounded-md bg-puntt-neutral-gray-3 px-2 py-1"
                data-testid={`ask-v2-quoted-context-${ctx.id}`}
              >
                <Text
                  size="1"
                  className="max-w-[200px] truncate text-puntt-neutral-gray-11"
                >
                  {ctx.text}
                </Text>
                <button
                  type="button"
                  data-testid={`ask-v2-remove-quoted-context-${ctx.id}`}
                  onClick={() => transport.removeQuotedContext(ctx.id)}
                  className="text-puntt-neutral-gray-9 hover:text-puntt-neutral-gray-12"
                  aria-label={t("askV2.transport.removeQuotedContext")}
                >
                  ×
                </button>
              </Flex>
            ))}
          </Flex>
        ) : null}
        {readOnly && !landing && (
          <Flex
            align="center"
            gap="1"
            px="3"
            py="1"
            className="border-b border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 text-xs text-puntt-neutral-gray-11"
            data-testid="ask-v2-readonly-banner"
          >
            <LockSimple size={12} weight="bold" />
            {/* Same `readOnlyChatNotice` key the v1 banner renders
                (routes/ask-puntt/chat.tsx), so the two surfaces cannot
                drift apart in any locale. The key is a top-level entry in
                i18n/locales/{en,es,pt,fr,de,ar}/ui.json (next to
                `readingAttachments`) — drop it from en and this banner
                renders the raw key, which the read-only case in
                ask-v2/pages/__tests__/AskV2ChatPage.test.tsx asserts
                against. */}
            <Text size="1" className="text-puntt-neutral-gray-11">
              {t("readOnlyChatNotice")}
            </Text>
          </Flex>
        )}
        {useMentionComposer ? (
          <MentionTextarea
            data-testid="ask-v2-mention-composer"
            value={transport.composer}
            members={mentionMembers ?? []}
            rosterMembers={rosterMembers}
            enableDirectorySearch={enableDirectoryMentions}
            composerAriaLabel={composerAriaLabel ?? placeholder}
            placeholder={placeholder}
            disabled={composerLocked}
            onChange={(value, nextMentions, nextEmails) => {
              transport.setComposer(value);
              setMentions(nextMentions);
              setMentionEmails(nextEmails);
            }}
            onSend={submit}
          />
        ) : (
          <TextArea
            data-testid="ask-v2-composer"
            size="3"
            variant="surface"
            placeholder={placeholder}
            value={transport.composer}
            disabled={composerLocked}
            onChange={(event) => transport.setComposer(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                submit();
              }
            }}
            className="w-full resize-none"
            style={{ height: landing ? "88px" : "72px" }}
          />
        )}
        {attachedItems.length > 0 && (
          <Flex
            gap="2"
            wrap="wrap"
            className="px-3 pb-1"
            data-testid="ask-v2-attached-files"
          >
            {attachedItems.map((item, idx) => (
              <Flex
                key={item.id}
                align="center"
                gap="1"
                className="rounded-md bg-puntt-neutral-gray-3 px-2 py-1"
                data-testid={`ask-v2-attached-file-${item.id}`}
              >
                {item.status === "pending" && (
                  <CircleNotch
                    size={12}
                    weight="bold"
                    className="shrink-0 animate-spin text-puntt-neutral-gray-9"
                  />
                )}
                <Text
                  size="1"
                  className="max-w-[150px] truncate text-puntt-neutral-gray-11"
                >
                  {item.file.name}
                  {item.status === "error" && " (failed)"}
                </Text>
                <button
                  type="button"
                  data-testid={`ask-v2-remove-attachment-${item.id}`}
                  onClick={() => removeFile(idx)}
                  className="text-puntt-neutral-gray-9 hover:text-puntt-neutral-gray-12"
                  aria-label={t("home.removeFileAriaLabel", {
                    name: item.file.name,
                  })}
                >
                  ×
                </button>
              </Flex>
            ))}
          </Flex>
        )}
        <Flex justify="between" align="center" className="p-1">
          <Flex align="center" gap="2">
            <AskV2AttachMenu
              disabled={
                readOnly ||
                transport.sending ||
                transport.inFlight ||
                projectSubmitting
              }
              onFilesSelected={addFiles}
              onScreenshotCaptured={addFile}
              webSearch={transport.webSearch}
              onWebSearchChange={transport.setWebSearch}
            />
            {reviewComposer?.leftPane != null &&
            reviewComposer.onPaneChange != null ? (
              <AskV2ReviewPaneTabs
                data-testid="ask-v2-composer-pane-tabs"
                testIdPrefix="ask-v2-composer"
                ariaLabel={t("askV2.review.composerPaneTabsLabel")}
                leftPane={reviewComposer.leftPane}
                commentCount={reviewComposer.commentCount ?? 0}
                onChange={reviewComposer.onPaneChange}
              />
            ) : null}
            {transport.webSearch ? (
              <Button
                type="button"
                size="1"
                variant="soft"
                className="cursor-pointer rounded-xl"
                data-testid="ask-v2-web-search-selected"
                onClick={() => transport.setWebSearch(false)}
                aria-label={t("askV2.transport.removeWebSearch")}
                disabled={
                  readOnly ||
                  transport.sending ||
                  transport.inFlight ||
                  projectSubmitting
                }
              >
                <Globe size={14} aria-hidden />
                {t("askV2.transport.webSearch")}
                <X size={12} aria-hidden />
              </Button>
            ) : null}
            {landing ? (
              <AskV2ComposerModeToggle
                data-testid="ask-v2-composer-mode"
                value={composerMode}
                onChange={setComposerMode}
              />
            ) : null}
          </Flex>
          {projectSubmitting ? (
            <Button
              type="button"
              size="2"
              className="cursor-default rounded-xl"
              data-testid="ask-v2-project-submitting"
              disabled
            >
              <CircleNotch size={14} weight="bold" className="animate-spin" />
            </Button>
          ) : transport.inFlight || transport.sending ? (
            // why: soft gray, not solid red — stop sits in the composer next to Ask; red solid reads as an alarm
            <Button
              type="button"
              variant="soft"
              color="gray"
              size="2"
              className="cursor-pointer rounded-xl transition-all duration-200"
              data-testid="ask-v2-stop"
              onClick={() => {
                void transport.abort();
              }}
              // `readOnly` is the same term the Ask button uses via
              // `canSend`. Without it this is the one write affordance in the
              // composer that still looks live for a read-only viewer, even
              // though the page-level gate turns its abort into a no-op.
              disabled={readOnly || transport.aborting}
            >
              <Stop size={14} weight="fill" />
              {t("askV2.transport.stop")}
            </Button>
          ) : (
            <Button
              type="submit"
              size="2"
              className="cursor-pointer rounded-xl"
              data-testid="ask-v2-ask"
              disabled={!canSend}
              aria-label={tCommon("nav.askPuntt")}
            >
              <ArrowUp size={14} weight="bold" />
              {tCommon("nav.askPuntt")}
            </Button>
          )}
        </Flex>
      </div>
    </form>
  );
}

function AskV2AttachMenu({
  disabled,
  onFilesSelected,
  onScreenshotCaptured,
  webSearch,
  onWebSearchChange,
}: {
  disabled: boolean;
  onFilesSelected: (files: File[]) => void;
  onScreenshotCaptured: (file: File) => void;
  webSearch: boolean;
  onWebSearchChange: (enabled: boolean) => void;
}) {
  const { t } = useTranslation("ui");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      onFilesSelected(Array.from(files));
      setMenuOpen(false);
    }
    event.target.value = "";
  };

  const handleScreenshot = async () => {
    // why: once getDisplayMedia resolves the browser IS capturing the user's
    // screen. Everything after it can throw — play() rejects with
    // NotAllowedError/AbortError on a detached element the browser refuses to
    // start, and toBlob can raise — and the stop() used to sit inside the try,
    // so a throw jumped straight to the swallowing catch and left every track
    // live: the capture keeps running with the "sharing your screen" indicator
    // lit until the tab is reloaded. Stop in finally so the grant always ends.
    // The happy path is unchanged: finally still runs before the queued toBlob
    // callback, and the pixels are already drawn into the canvas by then.
    let stream: MediaStream | null = null;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: "browser" } as MediaTrackConstraints,
      });
      const video = document.createElement("video");
      video.srcObject = stream;
      await video.play();

      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext("2d")?.drawImage(video, 0, 0);

      canvas.toBlob((blob) => {
        if (!blob) return;
        onScreenshotCaptured(
          new File([blob], `screenshot-${Date.now()}.png`, {
            type: "image/png",
          }),
        );
        setMenuOpen(false);
      }, "image/png");
    } catch {
      // User cancelled the display picker, or the browser refused it.
    } finally {
      stream?.getTracks().forEach((track) => track.stop());
    }
  };

  return (
    <Popover.Root
      open={menuOpen}
      onOpenChange={(next) => {
        if (disabled) return;
        setMenuOpen(next);
      }}
    >
      <Popover.Trigger>
        <IconButton
          type="button"
          variant="ghost"
          color="gray"
          size="2"
          disabled={disabled}
          className="!m-0 cursor-pointer rounded-xl"
          data-testid="ask-v2-attach-btn"
          aria-label={t("addAttachmentOrContext")}
        >
          <Plus size={16} />
        </IconButton>
      </Popover.Trigger>
      <Popover.Content
        side="top"
        align="start"
        className="max-h-[min(70vh,420px)] w-[260px] overflow-y-auto p-1"
      >
        <input
          ref={fileInputRef}
          type="file"
          aria-label={t("addFilesOrPhotos")}
          data-testid="ask-v2-attach-file-input"
          // why: Tailwind `hidden` is display:none; some browsers refuse to open a picker from that
          className="sr-only"
          accept={ASK_V2_FILE_ACCEPT.join(",")}
          onChange={handleFileSelect}
          multiple
        />
        <Button
          type="button"
          variant="ghost"
          color="gray"
          size="2"
          className="w-full cursor-pointer justify-start py-2 pl-5"
          onClick={() => fileInputRef.current?.click()}
          data-testid="ask-v2-add-files-btn"
        >
          <Paperclip size={16} />
          {t("addFilesOrPhotos")}
        </Button>
        <Button
          type="button"
          variant="ghost"
          color="gray"
          size="2"
          className="w-full cursor-pointer justify-start py-2 pl-5"
          onClick={() => {
            void handleScreenshot();
          }}
          data-testid="ask-v2-screenshot-btn"
        >
          <Camera size={16} />
          {t("takeScreenshot")}
        </Button>
        <Button
          type="button"
          variant="ghost"
          color="gray"
          size="2"
          className="w-full cursor-pointer justify-start py-2 pl-5"
          onClick={() => {
            onWebSearchChange(!webSearch);
            setMenuOpen(false);
          }}
          data-testid="ask-v2-web-search-menu-item"
          aria-pressed={webSearch}
        >
          <Globe size={16} className="text-cyan-600" aria-hidden />
          {t("askV2.transport.webSearch")}
          {webSearch ? (
            <Check size={16} className="ml-auto" aria-hidden />
          ) : null}
        </Button>
      </Popover.Content>
    </Popover.Root>
  );
}
